import React, { useState, useRef } from 'react';
import { useEmailSignatureContext } from 'contexts/EmailSignatureContext';
import { useAuth } from 'contexts/AuthContext';
import { useParams } from 'react-router-dom';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { toPng } from 'html-to-image';
import { AddIcon, KeyboardArrowDownIcon, DownloadIcon, SaveAltOutlinedIcon } from 'icons/materialIcons';
import TeamEmailSignatureContent from './TeamEmailSignatureContent';
import TeamAddToEmailSignatureModal from './TeamAddToEmailSignatureModal';
import { copyEmailSignatureHTML } from 'utils/emailSignature/copyEmailSignature';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';

import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Menu,
    MenuItem
} from '@mui/material';

const TeamEmailSignaturePreview = ({ user }) => {
    const { cardId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const signatureRef = useRef(null);
    const { emailSignatureState } = useEmailSignatureContext();
    const { authState } = useAuth();
    const userEmail = authState?.user?.email;

    const isAssigned = !!user?.assignedSignature;

    const handleCloseSuccessSnackbar = () => {
        setSuccessSnackbarOpen(false);
    };

    const handleDownloadImage = async () => {
        if (signatureRef.current) {
            setDownloading(true);
            handleClose();

            try {
                const scale = Math.min(
                    1350 / signatureRef.current.offsetWidth,
                    1428 / signatureRef.current.offsetHeight
                );

                const dataUrl = await toPng(signatureRef.current, {
                    width: 1350,
                    height: 1428,
                    style: {
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        width: `${signatureRef.current.offsetWidth}px`,
                        height: `${signatureRef.current.offsetHeight}px`,
                    }
                });

                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = `email-signature-${cardId}.png`;
                link.click();
            } catch (err) {
                console.error('Could not generate image:', err);
                setError('Failed to download signature image');
            } finally {
                setDownloading(false);
            }
        }
    };

    const handleCopyHTML = async () => {
        setDownloading(true);
        handleClose();

        try {
            if (!emailSignatureState[cardId]) {
                throw new Error('Email signature state not found');
            }

            await copyEmailSignatureHTML({
                state: emailSignatureState[cardId],
                contentRef: signatureRef,
                cardUrl: `${FRONTEND_BASE_URL}/card/${cardId}`
            });
            setSuccessSnackbarOpen(true);
        } catch (error) {
            console.error('Copy failed:', error);
            alert(`Failed to copy HTML code: ${error.message}`);
        } finally {
            setDownloading(false);
        }
    };

    const handleOpenAddToEmailSignatureModal = () => setModalOpen(true);
    const handleCloseAddToEmailSignatureModal = () => setModalOpen(false);
    const handleDownloadClick = (event) => !downloading && setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
        <Box sx={{
            height: isAssigned ? 'calc(100vh - 200px)' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isAssigned ? 'center' : 'flex-start',
            alignItems: isAssigned ? 'center' : 'stretch'
        }}>
            {!isAssigned && (
                <>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#828282", mb: "20px", mt: "30px", textAlign: 'center' }}>
                        Signature Preview
                    </Typography>

                    <Box sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
                        <TeamEmailSignatureContent
                            ref={signatureRef}
                            emailSignatureState={emailSignatureState}
                            cardId={cardId}
                            setLoading={setLoading}
                            setError={setError}
                        />
                    </Box>

                    {userEmail && (
                        <Typography variant="body2" sx={{
                            fontSize: '0.7rem',
                            color: "#828282",
                            mt: 2,
                            textAlign: 'center',
                            fontStyle: 'italic'
                        }}>
                            Preview based on {userEmail}
                        </Typography>
                    )}
                </>
            )}

            {isAssigned && (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: "#828282",
                        mb: 3,
                        px: 2,
                        maxWidth: '280px',
                        mx: 'auto'
                    }}>
                        Your admin has assigned you to an <br /> Email Signature Template.
                    </Typography>

                    {/* Hidden content for capture/reference if needed by download/copy functions */}
                    <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                        <TeamEmailSignatureContent
                            ref={signatureRef}
                            emailSignatureState={emailSignatureState}
                            cardId={cardId}
                            setLoading={setLoading}
                            setError={setError}
                        />
                    </Box>
                </Box>
            )}

            {loading && !isAssigned && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {error && (
                <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
                    {error}
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: isAssigned ? 0 : 3 }}>
                {isAssigned ? (
                    <WhiteButtonWithIcon
                        icon={SaveAltOutlinedIcon}
                        text="Save to your email"
                        onClick={handleOpenAddToEmailSignatureModal}
                        sx={{
                            width: '15rem', // 240px
                            borderRadius: '3.125rem', // 50px
                            padding: '0.625rem 1.25rem', // 10px 20px
                            '& .MuiButton-startIcon > *:first-of-type': {
                                fontSize: '1.25rem' // 20px
                            }
                        }}
                    />
                ) : (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddToEmailSignatureModal}
                        sx={{
                            backgroundColor: 'black',
                            color: 'white',
                            fontSize: '0.6rem',
                            fontWeight: '600',
                            borderRadius: '50px',
                            padding: '10px 30px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'black',
                            },
                        }}
                    >
                        Add to your email
                    </Button>
                )}

                <Button
                    onClick={handleDownloadClick}
                    endIcon={downloading ? null : <KeyboardArrowDownIcon />}
                    disabled={downloading}
                    sx={{
                        color: '#2196f3',
                        textTransform: 'none',
                        background: 'transparent',
                        '&:hover': {
                            background: 'transparent',
                        },
                        minWidth: '100px',
                        padding: '6px 8px',
                    }}
                >
                    {downloading ? (
                        <>
                            <CircularProgress size={16} sx={{ color: '#2196f3', marginRight: 1 }} />
                            Downloading...
                        </>
                    ) : (
                        'Download'
                    )}
                </Button>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && !downloading}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <MenuItem onClick={handleDownloadImage} disabled={downloading}>
                        Download Signature Image
                    </MenuItem>
                    <MenuItem onClick={handleCopyHTML} disabled={downloading}>
                        Copy Signature HTML
                    </MenuItem>
                </Menu>

                <TeamAddToEmailSignatureModal
                    open={modalOpen}
                    onClose={handleCloseAddToEmailSignatureModal}
                    contentRef={signatureRef}
                />
            </Box>

            <TeamNotificationSnackbar
                open={successSnackbarOpen}
                severity="info"
                message="Email signature copied to clipboard."
                handleClose={handleCloseSuccessSnackbar}
                sx={{ zIndex: 1301 }}
            />
        </Box>
    );
};

export default TeamEmailSignaturePreview;